import React from 'react';
import { Banner, Page, Layout, Card, Button, BlockStack } from '@shopify/polaris';
import './ErrorBoundary.css';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the whole app
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="app-error-fallback">
          <Page>
            <Layout>
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <Banner
                      title="Something went wrong"
                      tone="critical"
                    >
                      <p>The application encountered an unexpected error. Please try refreshing the page.</p>
                    </Banner>

                    {this.state.error && (
                      <details className="app-error-details">
                        <summary>Error Details</summary>
                        <pre>
                          {this.state.error.toString()}
                          {this.state.errorInfo && (
                            <>
                              {'\n\n'}
                              {this.state.errorInfo.componentStack}
                            </>
                          )}
                        </pre>
                      </details>
                    )}

                    <Button
                      primary
                      onClick={this.handleReset}
                    >
                      Try Again
                    </Button>

                    <Button
                      onClick={() => window.location.reload()}
                    >
                      Refresh Page
                    </Button>
                  </BlockStack>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
